import { useEffect, useState } from 'react';

// Kiki's shift and event bookings are made through a reservation widget that
// lives on the Kiki's website — both embedded on the site and via the
// standalone widget link. After a successful payment we keep the guest on the
// confirmation for a moment so they can read it, then send them back to the
// Kiki's profile page (Experiences tab) where they booked.
//
// Gating purely on the restaurant id means this applies regardless of where the
// booking originated (embedded widget vs external widget link) and never affects
// any other restaurant. Configured per deployment via env:
//   REACT_APP_KIKIS_RESTAURANT_ID — backend id of the Kiki's restaurant (default 14)
//   REACT_APP_KIKIS_SITE_URL      — base URL of the Kiki's site; blank disables the redirect
export const KIKIS_RESTAURANT_ID = Number(process.env.REACT_APP_KIKIS_RESTAURANT_ID || 14);

// Defaults to the live Kiki's site so production works without extra build env.
// Any deployment that sets REACT_APP_KIKIS_SITE_URL (e.g. staging →
// stagingkikis.itstali.com) overrides this; set it to a single space (or any
// value) only if you ever need to disable the redirect for a given build.
export const KIKIS_SITE_URL = process.env.REACT_APP_KIKIS_SITE_URL || 'https://kikisexperience.com';

// How long the success page stays on screen before the redirect fires.
export const KIKIS_REDIRECT_DELAY_MS = 3000;

export const isKikisRestaurant = (restaurantId) => Number(restaurantId) === KIKIS_RESTAURANT_ID;

// True when a confirmed Kiki's booking should bounce back to the Kiki's site.
// Requires the site URL to be configured (blank disables the redirect entirely,
// e.g. local dev without a Kiki's site running).
export const shouldRedirectToKikis = (restaurantId) =>
  Boolean(KIKIS_SITE_URL) && isKikisRestaurant(restaurantId);

// Absolute URL of the Kiki's profile page, pre-selecting the Experiences tab
// (where shift/event reservations live).
export const kikisProfileUrl = () => {
  const base = KIKIS_SITE_URL.replace(/\/+$/, '');
  return `${base}/profile?tab=experiences`;
};

// Absolute URL of a single experience's booking page on the Kiki's site. Kiki's
// has its own standalone event page (route `/events/:eventId/reserve`), so when
// an experience is set to "book through page" we send Kiki's guests there
// instead of Tali's generic /experience/:id page.
export const kikisEventReserveUrl = (eventId) => {
  const base = KIKIS_SITE_URL.replace(/\/+$/, '');
  return `${base}/events/${eventId}/reserve`;
};

// True when an experience banner for this restaurant should open the Kiki's own
// site rather than the Tali standalone experience page. Requires the site URL to
// be configured (blank disables it, e.g. local dev without a Kiki's site).
export const shouldOpenKikisEventPage = (restaurantId) =>
  Boolean(KIKIS_SITE_URL) && isKikisRestaurant(restaurantId);

// Navigate the whole browser to a Kiki's experience page. Prefer the top window
// so an embedded widget breaks out of its iframe (cross-origin assignment is
// allowed even though reading is not), with a same-frame fallback.
export const redirectToKikisEvent = (eventId) => {
  const url = kikisEventReserveUrl(eventId);
  try {
    window.top.location.href = url;
  } catch (e) {
    window.location.href = url;
  }
};

// Navigate the whole browser to the Kiki's profile. Prefer the top window so an
// embedded widget breaks out of its iframe; assigning a cross-origin location is
// permitted even though reading it is not, with a same-frame fallback.
export const redirectToKikisProfile = () => {
  const url = kikisProfileUrl();
  try {
    window.top.location.replace(url);
  } catch (e) {
    window.location.replace(url);
  }
};

// When `active` becomes true (a confirmed Kiki's booking is on screen), show the
// success page for KIKIS_REDIRECT_DELAY_MS, then redirect to the Kiki's profile.
// Returns whether a redirect is currently pending so callers can show a notice.
export const useKikisProfileRedirect = (active) => {
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    if (!active) return undefined;
    setRedirecting(true);
    const timer = setTimeout(redirectToKikisProfile, KIKIS_REDIRECT_DELAY_MS);
    return () => clearTimeout(timer);
  }, [active]);

  return redirecting;
};
